import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { ContractsFilterForShipper } from 'src/apis/capacity-management/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

export const contractOptionSchema = z.object({
  tsoShortName: z.string(),
  ...formOptionSchema.shape,
});

type ContractOption = z.input<typeof contractOptionSchema>;

export const ContractsSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'contractId',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<ContractsFilterForShipper>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  const contracts = searchRequest.response?.data._embedded.items.map(
    (contract) =>
      ({
        value: contract.id,
        label: contract.assignedId,
        tsoShortName: contract.tsoShortName,
      }) satisfies ContractOption
  );

  const search = useClientSearch(contracts, (contract, search) =>
    contract.label.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.tsoShortName}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>TSO:</strong> {option.tsoShortName}
        </>
      )}
    />
  );
};
